/**
 * Payment Success Page
 * LOT 4 - Système de Paiement Stripe
 * 
 * Page affichée après un paiement réussi
 * Effectue le polling pour vérifier le statut
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  CheckCircle2, Loader2, AlertTriangle, 
  Calendar, MapPin, Car, Receipt, Home, User
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from 'config';

const API_URL = config.API_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const bookingNumberFromUrl = searchParams.get('booking_number');
  const paymentMethod = searchParams.get('method');
  
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Handle cash payment or driver payment - fetch booking details
    if ((paymentMethod === 'cash' || paymentMethod === 'driver') && bookingId) {
      fetchBookingDetails(bookingId, paymentMethod, bookingNumberFromUrl);
      return;
    }
    
    // Handle Stripe payment - poll for status
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, paymentMethod, bookingId, bookingNumberFromUrl]);

  const fetchBookingDetails = async (id, method, urlBookingNumber) => {
    try {
      // Try to get booking details to retrieve the booking_number
      const response = await axios.get(`${API_URL}/api/bookings/${id}`);
      const booking = response.data;
      setPaymentData({
        booking_id: id,
        booking_number: booking.booking_number || urlBookingNumber,
        payment_method: method,
        payment_status: 'pending',
        ...booking
      });
      setStatus('cash_success');
    } catch (err) {
      // Fallback if we can't fetch details - use booking_number from URL
      setPaymentData({
        booking_id: id,
        booking_number: urlBookingNumber,
        payment_method: method,
        payment_status: 'pending'
      });
      setStatus('cash_success');
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError(err.response?.data?.detail || 'Erreur de vérification du paiement');
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center py-8">
            <Loader2 className="w-16 h-16 text-[#C9A24D] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold text-[#E5E5E5] mb-2">
              Vérification du paiement...
            </h2>
            <p className="text-[#E5E5E5]/60">
              Veuillez patienter pendant que nous confirmons votre paiement
            </p>
          </div>
        );

      case 'cash_success':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
              Réservation confirmée !
            </h2>
            <p className="text-[#E5E5E5]/60 mb-6">
              Votre réservation a été enregistrée avec succès
            </p>

            {paymentData && (
              <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 text-left mb-6">
                <CardContent className="p-6 space-y-4">
                  {/* Prominent booking reference */}
                  <div className="text-center py-4 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg mb-4">
                    <span className="text-[#E5E5E5]/60 text-sm block mb-1">Votre numéro de réservation</span>
                    <span className="text-[#C9A24D] font-mono font-bold text-2xl tracking-wider">
                      {paymentData.booking_number || paymentData.booking_id?.slice(-8).toUpperCase()}
                    </span>
                    <p className="text-[#E5E5E5]/40 text-xs mt-2">Conservez ce numéro pour suivre votre réservation</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#E5E5E5]/60">Mode de paiement</span>
                    <span className="text-[#E5E5E5] font-semibold">
                      Paiement au chauffeur
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[#E5E5E5]/60">Statut</span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm">
                      En attente de confirmation
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="p-4 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg text-sm text-[#C9A24D] mb-6">
              Un email de confirmation vous sera envoyé prochainement. Le paiement sera effectué directement au chauffeur.
            </div>

            <div className="flex flex-col gap-3">
              {/* Bouton Mon Espace - uniquement pour les utilisateurs connectés */}
              {isAuthenticated && user && (
                <Button
                  onClick={() => navigate('/client?success=true')}
                  className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                >
                  <User className="w-4 h-4 mr-2" />
                  Accéder à mon espace
                </Button>
              )}
              <Button
                onClick={() => navigate('/suivi')}
                variant="outline"
                className="w-full border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Suivre ma réservation
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant={isAuthenticated ? "ghost" : "default"}
                className={isAuthenticated 
                  ? "w-full text-[#E5E5E5]/60 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10" 
                  : "w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                }
              >
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
              Paiement confirmé !
            </h2>
            <p className="text-[#E5E5E5]/60 mb-6">
              Votre réservation a été enregistrée avec succès
            </p>

            {paymentData && (
              <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 text-left mb-6">
                <CardContent className="p-6 space-y-4">
                  {/* Prominent booking reference */}
                  <div className="text-center py-4 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg mb-4">
                    <span className="text-[#E5E5E5]/60 text-sm block mb-1">Votre numéro de réservation</span>
                    <span className="text-[#C9A24D] font-mono font-bold text-2xl tracking-wider">
                      {paymentData.booking_number || paymentData.booking_id?.slice(-8).toUpperCase()}
                    </span>
                    <p className="text-[#E5E5E5]/40 text-xs mt-2">Conservez ce numéro pour suivre votre réservation</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#E5E5E5]/60">Montant payé</span>
                    <span className="text-[#E5E5E5] font-semibold">
                      {paymentData.amount?.toFixed(2)}€
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[#E5E5E5]/60">Statut</span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                      <CheckCircle2 className="w-3 h-3" />
                      Confirmé
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="p-4 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg text-sm text-[#C9A24D] mb-6">
              Un email de confirmation vous sera envoyé prochainement.
            </div>

            <div className="flex flex-col gap-3">
              {/* Bouton Mon Espace - uniquement pour les utilisateurs connectés */}
              {isAuthenticated && user && (
                <Button
                  onClick={() => navigate('/client?success=true')}
                  className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                >
                  <User className="w-4 h-4 mr-2" />
                  Accéder à mon espace
                </Button>
              )}
              <Button
                onClick={() => navigate('/suivi')}
                variant="outline"
                className="w-full border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Suivre ma réservation
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant={isAuthenticated ? "ghost" : "default"}
                className={isAuthenticated 
                  ? "w-full text-[#E5E5E5]/60 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10" 
                  : "w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                }
              >
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-500/20 mb-6">
              <AlertTriangle className="w-10 h-10 text-orange-400" />
            </div>
            
            <h2 className="text-xl font-semibold text-[#E5E5E5] mb-2">
              Session expirée
            </h2>
            <p className="text-[#E5E5E5]/60 mb-6">
              La session de paiement a expiré. Veuillez recommencer.
            </p>

            <Button
              onClick={() => navigate('/booking')}
              className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
            >
              Recommencer une réservation
            </Button>
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-yellow-500/20 mb-6">
              <Loader2 className="w-10 h-10 text-yellow-400" />
            </div>
            
            <h2 className="text-xl font-semibold text-[#E5E5E5] mb-2">
              Vérification en cours
            </h2>
            <p className="text-[#E5E5E5]/60 mb-6">
              Le statut du paiement est en cours de traitement. 
              Veuillez vérifier votre email ou réessayer plus tard.
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setAttempts(0);
                  setStatus('checking');
                  pollPaymentStatus();
                }}
                className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-[#E5E5E5]/20 text-[#E5E5E5]"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/20 mb-6">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            
            <h2 className="text-xl font-semibold text-[#E5E5E5] mb-2">
              Erreur de vérification
            </h2>
            <p className="text-[#E5E5E5]/60 mb-6">
              {error || "Une erreur est survenue lors de la vérification du paiement."}
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setError(null);
                  setAttempts(0);
                  setStatus('checking');
                  pollPaymentStatus();
                }}
                className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-[#E5E5E5]/20 text-[#E5E5E5]"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="bg-[#0F0F0F] border-[#C9A24D]/20">
          <CardContent className="p-6">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
